/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * PaperSheet de cabecera de módulo: kicker + título + meta + tools (search / acciones).
 * El título de módulo vive acá (chrome MainTabs ya no lo muestra).
 */
import React from 'react';
import { StyleSheet, Text, View, type StyleProp, type ViewStyle } from 'react-native';
import { ticketColors, ticketFonts } from '../theme/public-api';
import { PaperSheet } from './PaperSheet';

export type TicketModuleHeaderProps = {
  /** Ej. "Inventario" / "Catálogo" en mono uppercase. */
  kicker?: string;
  title: string;
  meta?: string;
  /** CTA primario a la derecha del título (ej. Nuevo producto en desktop). */
  trailing?: React.ReactNode;
  /** Fila de search + botones debajo del título. */
  tools?: React.ReactNode;
  /** Contenido extra bajo tools (chips, sheets host, etc.). */
  children?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  /** Override del padding interno (ej. POS desk alinea gutter con grilla). */
  padStyle?: StyleProp<ViewStyle>;
  /** Oculta kicker/título/meta (ej. POS desk: el buscador es el héroe). */
  hideTitleBlock?: boolean;
  testID?: string;
};

export function TicketModuleHeader({
  kicker,
  title,
  meta,
  trailing,
  tools,
  children,
  style,
  padStyle,
  hideTitleBlock = false,
  testID,
}: TicketModuleHeaderProps) {
  const showTitleBlock = !hideTitleBlock && (kicker || title || meta || trailing);
  return (
    <PaperSheet style={style} testID={testID}>
      <View style={[styles.pad, padStyle]}>
        {showTitleBlock ? (
          <View style={styles.titleRow}>
            <View style={styles.titleBlock}>
              {kicker ? <Text style={styles.kicker}>{kicker}</Text> : null}
              <Text style={styles.title} numberOfLines={1}>
                {title}
              </Text>
              {meta ? (
                <Text style={styles.meta} numberOfLines={2}>
                  {meta}
                </Text>
              ) : null}
            </View>
            {trailing ? <View style={styles.trailing}>{trailing}</View> : null}
          </View>
        ) : null}
        {tools ? <View style={[styles.tools, !showTitleBlock && styles.toolsFlush]}>{tools}</View> : null}
        {children}
      </View>
    </PaperSheet>
  );
}

const styles = StyleSheet.create({
  pad: {
    padding: 14,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  titleBlock: {
    flex: 1,
    minWidth: 0,
  },
  trailing: {
    flexShrink: 0,
    paddingTop: 2,
  },
  kicker: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  title: {
    marginTop: 2,
    fontSize: 20,
    fontWeight: '800',
    letterSpacing: -0.4,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  meta: {
    marginTop: 4,
    fontSize: 13,
    lineHeight: 18,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  tools: {
    marginTop: 12,
    gap: 8,
  },
  toolsFlush: {
    marginTop: 0,
  },
});
